import type { ReactNode } from 'react'
import { lazy, Suspense, useMemo } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { useCapability } from '../hooks/useCapability'
import { can, type CapabilityKey } from '../lib/roles'
// FRONTDOOR-SPEED-1 — LAZY, both of them. AuthGuard is statically imported by
// App.tsx, so these two notices were in the entry chunk every stranger
// downloads, for screens that render only when access is refused. They were
// three of the five eager paths from App.tsx to the 598 KB icon barrel.
const AccessDeniedNotice = lazy(() =>
  import('./auth/AccessDeniedNotice').then((m) => ({ default: m.AccessDeniedNotice })),
)
import { SECTIONS, filterSectionsByRole } from '../lib/navConfig'
import { pathVerdict } from '../lib/navAccountLayer'
import { NavSkeleton } from './layout/NavSkeleton'
import { useResolvedNav } from '../hooks/useNavSections'
const HiddenModuleNotice = lazy(() =>
  import('./layout/HiddenModuleNotice').then((m) => ({ default: m.HiddenModuleNotice })),
)

interface AuthGuardProps {
  children: ReactNode
  // U1b: optional page-level capability gate. When set, a signed-in user who
  // lacks the capability sees AccessDeniedNotice instead of the page.
  //
  // The check mirrors the nav gate EXACTLY — role-only `can(role, key)`, the
  // same call navConfig's `gate` uses — so page access equals nav visibility:
  // no "see the link, get bounced" (a page can't be stricter than its nav
  // entry) and no unguarded direct-URL pages (a nav-gated page can't be
  // reached by a role the nav hides it from). Per-member capability OVERRIDES
  // are intentionally not consulted here so this stays identical to the nav;
  // overrides remain enforced server-side by RLS (the real security backstop).
  requireCapability?: CapabilityKey
  // Minimum level the capability must satisfy (defaults to 'view', matching
  // navConfig's default). Rarely needed; present for parity with can().
  minCapabilityLevel?: 'full' | 'own' | 'view'
}

// Phase L1: a pure auth gate. Onboarding-step routing lives in
// OnboardingRedirector (mounted inside AppShell).
//
// U1b: gained the optional `requireCapability` role gate above. When no
// capability is passed the behavior is unchanged (auth-only), so every
// existing call site stays backward compatible. This is now the single
// canonical page-guard mechanism — pages no longer hand-roll owner/admin
// literals.
export function AuthGuard({ children, requireCapability, minCapabilityLevel }: AuthGuardProps) {
  const { user, loading } = useAuth()
  const capability = useCapability()

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-sm text-muted-foreground">Loading...</div>
      </div>
    )
  }

  if (!user) {
    return <Navigate to="/login" replace />
  }

  if (requireCapability && !capability.can(requireCapability, minCapabilityLevel ?? 'view')) {
    return (
      <Suspense fallback={null}>
        <AccessDeniedNotice />
      </Suspense>
    )
  }

  // PLATFORM-1 — ONE GUARD AFTER THE CAPABILITY GUARD, and the order matters.
  //
  // "You are not allowed here" and "this business does not use this" are
  // different sentences with different remedies, and the first must win: a
  // technician who cannot see payouts should be told that, not told the
  // business switched payouts off.
  //
  // This sits in AuthGuard rather than on individual pages because EVERY
  // dashboard route already passes through it. That is what catches the
  // hundreds of route literals scattered across the product without rewriting
  // one of them - they all land on a route, and every route comes through here.
  return <HiddenModuleGuard>{children}</HiddenModuleGuard>
}

/**
 * Renders the notice when this account has hidden the module the current route
 * belongs to, and the page itself otherwise.
 *
 * A route no navigation node owns is never hidden - the guard cannot know
 * which module it belongs to, so it does not guess. Those routes are listed by
 * name in navAccountLayer.test.ts.
 */
function HiddenModuleGuard({ children }: { children: React.ReactNode }) {
  const location = useLocation()
  const { currentTeamRole, profile } = useAuth()
  const { overrides } = useCapability()
  // SHELF-FLASH-2 — the guard reads the resolved nav, not the sections alone,
  // so it can tell "not asked yet" from "hidden". Answering `hidden` during the
  // read told merchants they had hidden a section they never touched.
  const nav = useResolvedNav()

  const isAdmin = can.viewPlatformAdmin(profile)
  // The ceiling: everything this ROLE may see, before the account subtracted
  // anything. Comparing the two is what tells hidden-by-account apart from
  // hidden-by-role, which the capability guard above already handled.
  const roleVisible = useMemo(
    () => filterSectionsByRole(SECTIONS, currentTeamRole, isAdmin, overrides),
    [currentTeamRole, isAdmin, overrides],
  )

  const verdict = pathVerdict(nav, roleVisible, location.pathname)
  // The same skeleton the sidebar shows. A route is never rendered, and never
  // refused, before we know which it is.
  if (verdict.state === 'unknown') return <NavSkeleton label="Loading this page" />
  if (verdict.state === 'hidden') return <HiddenModuleNotice label={verdict.label} />
  return <>{children}</>
}
